import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, FileText, PackageCheck, ShoppingCart, Trash2, X } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { formatINRSymbol } from '@/lib/format';
import { useCart } from '@/cart/useCart';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CartDrawer({ open, onClose }: Props) {
  const cart = useCart();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const navigate = useNavigate();
  const [busyItemId, setBusyItemId] = useState<number | null>(null);
  const [clearing, setClearing] = useState(false);

  if (!open) return null;

  const items = cart.cart?.items ?? [];
  const isEmpty = items.length === 0;

  async function onRemove(itemId: number) {
    setBusyItemId(itemId);
    try {
      await cart.remove(itemId);
    } catch {
      toast.push('Could not remove item', 'error');
    } finally {
      setBusyItemId(null);
    }
  }

  async function onSetQty(itemId: number, qtyStr: string) {
    const qty = Number(qtyStr);
    if (!Number.isFinite(qty) || qty <= 0) return;
    setBusyItemId(itemId);
    try {
      await cart.setQty(itemId, qty);
    } catch {
      toast.push('Could not update quantity', 'error');
    } finally {
      setBusyItemId(null);
    }
  }

  async function onClearAll() {
    if (!await confirmDlg('Remove all items from the cart?')) return;
    setClearing(true);
    try {
      await cart.clear();
      toast.push('Cart cleared', 'info');
    } catch {
      toast.push('Could not clear cart', 'error');
    } finally {
      setClearing(false);
    }
  }

  function onProceedEnquiry() {
    onClose();
    // Open the Enquiry Dashboard's "Log enquiry" form pre-filled with the cart.
    navigate('/sales/enquiry-dashboard?fromCart=1');
  }

  function onProceedOrder() {
    onClose();
    navigate('/sales/order-summary?fromCart=1');
  }

  // Hand the cart's items (with their quantities) to the MRN page, which opens
  // the New MRN form pre-filled so the normal requisition flow continues.
  function onRaiseMrn() {
    const mrnSeed = items.map((l) => ({
      itemId: l.itemId,
      itemName: l.item.name,
      qty: l.qty,
      unit: l.item.unit,
    }));
    onClose();
    navigate('/purchase/mrn', { state: { mrnSeed } });
  }

  return (
    <div
      className="fixed inset-0 z-[500] flex justify-end"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="flex h-full w-full max-w-md flex-col shadow-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between border-b px-3 py-2"
          style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}
        >
          <div className="flex items-center gap-1.5">
            <ShoppingCart size={14} className="text-accent" />
            <div className="text-[13px] font-extrabold" style={{ color: 'var(--text-primary)' }}>
              Cart · {items.length} item{items.length === 1 ? '' : 's'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-6 w-6 items-center justify-center rounded-md opacity-60 hover:opacity-100"
            style={{ background: 'var(--bg-soft)' }}
            title="Close"
          >
            <X size={12} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-2 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {isEmpty ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm">
              <ShoppingCart size={36} className="opacity-30" />
              <div className="text-[12px] font-semibold text-[var(--text-secondary)]">Your cart is empty</div>
              <div className="text-[11px] text-[var(--text-muted)]">
                Add items from the Product Catalogue.
              </div>
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {items.map((line) => {
                const lineTotal = line.item.mrp * line.qty;
                const busy = busyItemId === line.itemId;
                return (
                  <li
                    key={line.id}
                    className="flex flex-col gap-1 rounded-md border border-[var(--border-color)] bg-white/[0.02] p-2"
                  >
                    {/* Row 1: Name + remove */}
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[12px] font-bold" title={line.item.name}>
                          {line.item.name}
                        </div>
                        <div className="truncate text-[10px] text-[var(--text-muted)]" title={`${line.item.code} · ${line.item.category}`}>
                          {line.item.code} · {line.item.category}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemove(line.itemId)}
                        disabled={busy}
                        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-danger to-[#dc2626] text-white shadow-sm hover:brightness-110 disabled:opacity-40"
                        title="Remove from cart"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                    {/* Row 2: Qty · Rate · Line total */}
                    <div className="flex items-center gap-2 text-[10px]">
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        value={String(line.qty)}
                        onChange={(e) => onSetQty(line.itemId, e.target.value)}
                        disabled={busy}
                        className="!h-6 !w-16 flex-shrink-0 text-right !text-[11px]"
                      />
                      <span className="text-[var(--text-muted)]">{line.item.unit}</span>
                      <span className="text-[var(--text-muted)]">× {formatINRSymbol(line.item.mrp)}</span>
                      <span className="ml-auto text-[12px] font-black text-accent">
                        {formatINRSymbol(lineTotal)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        {!isEmpty && (
          <div
            className="border-t px-3 py-2"
            style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}
          >
            <div className="mb-1.5 flex items-center justify-between text-[11px]">
              <span className="text-[var(--text-muted)]">Estimated total (MRP)</span>
              <span className="text-[14px] font-black text-accent">
                {formatINRSymbol(items.reduce((s, l) => s + l.item.mrp * l.qty, 0))}
              </span>
            </div>
            <div className="grid grid-cols-[auto,1fr,1fr,1fr] gap-1.5">
              <Button
                variant="danger"
                size="sm"
                onClick={onClearAll}
                disabled={clearing}
                title="Remove all items"
              >
                <Trash2 size={13} />
              </Button>
              <Button
                variant="glass"
                size="sm"
                onClick={onRaiseMrn}
                title="Raise a Material Requisition (MRN) for these items"
              >
                <span className="inline-flex items-center gap-1"><ClipboardList size={12} /> MRN</span>
              </Button>
              <Button
                variant="glass"
                size="sm"
                onClick={onProceedEnquiry}
                title="Save as an enquiry — send quotation, non-committal"
              >
                <span className="inline-flex items-center gap-1"><FileText size={12} /> Enquiry</span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onProceedOrder}
                title="Firm order — customer is committing to buy"
              >
                <span className="inline-flex items-center gap-1"><PackageCheck size={12} /> Place Order</span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
